import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import {
  Leaf,
  Compass,
  Crown,
  Check,
  Sparkles,
  ArrowLeft,
  Star,
  Heart,
  Loader2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import CurrencySelector from './CurrencySelector';

const UpgradePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(null);
  const [tiers, setTiers] = useState([]);
  const { formatPrice, currency } = useCurrency();
  const currentTier = user?.tier || 'free';

  useEffect(() => {
    fetch(`${API_URL}/api/tiers`)
      .then(r => r.json())
      .then(data => setTiers(data))
      .catch(() => {});
  }, []);

  const tierIcons = { free: Leaf, basic: Compass, premium: Crown, faith_builder: Package };
  const tierColorMap = {
    free: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'bg-emerald-100 text-emerald-600', button: 'bg-emerald-500 hover:bg-emerald-600' },
    basic: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', icon: 'bg-sky-100 text-sky-600', button: 'bg-sky-500 hover:bg-sky-600' },
    premium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'bg-amber-100 text-amber-600', button: 'bg-amber-500 hover:bg-amber-600' },
    faith_builder: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'bg-purple-100 text-purple-600', button: 'bg-purple-500 hover:bg-purple-600' }
  };

  const tierOrder = { free: 0, basic: 1, premium: 2, faith_builder: 3 };

  const handleUpgrade = async (tierId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setLoading(tierId);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/membership`,
        { tier: tierId, origin_url: window.location.origin },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const handleDonate = async (donationType) => {
    setLoading('donate');
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/donate`,
        { donation_type: donationType, origin_url: window.location.origin },
        { headers: getAuthHeaders ? getAuthHeaders() : {}, withCredentials: true }
      );
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <Button variant="ghost" onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isAuthenticated ? 'Back to Library' : 'Back to Home'}
          </Button>
          <div className="flex justify-center mb-4">
            <CurrencySelector />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Unlock More Adventures!</span>
          </div>
          <h1 className="text-4xl font-serif font-bold text-slate-800 mb-4">Support Our Mission</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your donation subscription helps us create free faith-based resources for children. Choose a level to support our work and unlock exclusive features!
          </p>
        </div>

        <div className={`grid gap-6 lg:gap-8 mb-16 ${tiers.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
          {tiers.map((tier) => {
            const colors = tierColorMap[tier.tier_id] || tierColorMap.basic;
            const TierIcon = tierIcons[tier.tier_id] || Leaf;
            const isCurrent = tier.tier_id === currentTier;
            const currentLevel = tierOrder[currentTier] || 0;
            const tierLevel = tierOrder[tier.tier_id] || tier.sort_order || 0;
            const isDowngrade = tierLevel < currentLevel;
            const isPopular = tier.tier_id === 'basic';
            const priceDisplay = tier.price === 0 ? 'Free' : formatPrice(tier.price);

            return (
              <div
                key={tier.tier_id}
                className={`relative rounded-3xl p-6 lg:p-8 border-2 transition-all duration-300 ${
                  isPopular ? 'ring-2 ring-sky-500 ring-offset-2' : ''
                } ${colors.bg} ${colors.border} ${
                  isCurrent ? 'scale-105 shadow-xl' : 'hover:shadow-lg'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="flex items-center gap-1 px-3 py-1 bg-sky-500 text-white text-xs font-bold rounded-full">
                      <Star className="w-3 h-3" fill="currentColor" /> Most Popular
                    </div>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <div className="px-3 py-1 bg-slate-800 text-white text-xs font-bold rounded-full">Current Plan</div>
                  </div>
                )}

                <div className={`w-16 h-16 rounded-2xl ${colors.icon} flex items-center justify-center mb-6`}>
                  <TierIcon className="w-8 h-8" />
                </div>

                <h3 className="text-2xl font-bold text-slate-800 mb-1">{tier.name}</h3>
                <p className="text-slate-600 text-sm mb-4">{tier.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-800">{priceDisplay}</span>
                  {tier.price > 0 && <span className="text-slate-500 text-sm ml-1">per month</span>}
                </div>

                <ul className="space-y-3 mb-8">
                  {(tier.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-xl py-6 text-white font-medium ${
                    isCurrent || tier.tier_id === 'free' ? 'bg-slate-400 cursor-not-allowed' : isDowngrade ? 'bg-slate-300 text-slate-600' : colors.button
                  }`}
                  disabled={isCurrent || isDowngrade || tier.tier_id === 'free' || loading === tier.tier_id}
                  onClick={() => handleUpgrade(tier.tier_id)}
                >
                  {loading === tier.tier_id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isCurrent ? 'Current Plan' : tier.tier_id === 'free' ? 'Free Forever' : isDowngrade ? 'Downgrade' : (
                    `Upgrade to ${tier.name}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Donation Section */}
        <div id="support-us" className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 lg:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-coral-500" fill="currentColor" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Support Our Mission</h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Your donation helps us provide free faith-based resources to children in schools, hospitals, and churches.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {[{ type: 'small', amount: '£5' }, { type: 'medium', amount: '£10' }, { type: 'large', amount: '£25' }, { type: 'generous', amount: '£50' }].map((donation) => (
              <Button
                key={donation.type}
                variant="outline"
                onClick={() => handleDonate(donation.type)}
                disabled={loading === 'donate'}
                className="h-20 rounded-2xl border-2 border-coral-200 hover:border-coral-400 hover:bg-coral-50 flex flex-col items-center justify-center gap-1"
              >
                <span className="text-2xl font-bold text-coral-600">{donation.amount}</span>
                <span className="text-xs text-slate-500">One-time</span>
              </Button>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500 mt-6">All donations go directly to supporting children's faith education.</p>
        </div>

        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">Secure payments powered by Stripe. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
